import PropTypes from 'prop-types';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Checkbox,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useProject } from '../../Layout/header/context';
import { useStyles } from '../styles/moreDetailsStyles';
import { PROJECT_STATUS_LIST } from '../../../../Constants/ProjectConstants';
import { getAllClients } from '../../../../services/clientService';
import { updateProject } from '../../../../services/projectService';

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const MoreDetails = () => {

  const { projectDetails, setProjectDetails, isEditPermission } = useProject();
  const classes = useStyles();
  const [clientList, setClientList] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [enteredTotalHrs, setEnteredTotalHrs] = useState(projectDetails?.totalHrs);
  const [enteredInitialEstimatedHrs, setEnteredInitialEstimatedHrs] = useState(projectDetails?.initialEstimatedHrs);

  const handleClientChange = async (e, value) => {
    if (!isEditPermission || value?.id === projectDetails?.clientId) return;
    try {
      setSelectedClient(value);
      setDisabled('clientId');
      await updateProject(projectDetails?.id, { clientId: value?.id });
      setProjectDetails((prev) => ({ ...prev, clientId: value?.id }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    if (!isEditPermission || status === projectDetails?.status) return;
    try {
      setDisabled('status');
      await updateProject(projectDetails?.id, { status });
      setProjectDetails((prev) => ({ ...prev, status }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const hanldeTotalHrsChange = (e) => {
    const inputValue = e.target.value;
    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue); // Check if input matches the pattern for valid decimal/float values

    if (isValidInput && inputValue >= 0) {
      // Check if input is valid and not negative
      setEnteredTotalHrs(inputValue);
    }
  };

  const handleTotalHrsBlur = async () => {
    const totalHrs = parseFloat(enteredTotalHrs);
    if (!isEditPermission || totalHrs === projectDetails?.totalHrs) return;
    try {
      setDisabled('totalHrs');
      await updateProject(projectDetails?.id, { totalHrs });
      setProjectDetails((prev) => ({ ...prev, totalHrs }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleInitialEstimatedHrsChange = (e) => {
    const inputValue = e.target.value;
    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue); // Check if input matches the pattern for valid decimal/float values

    if (isValidInput && inputValue >= 0) {
      // Check if input is valid and not negative
      setEnteredInitialEstimatedHrs(inputValue);
    }
  };

  const handleInitialEstimatedHrsBlur = async () => {
    const initialEstimatedHrs = parseFloat(enteredInitialEstimatedHrs);
    if (!isEditPermission || initialEstimatedHrs === projectDetails?.initialEstimatedHrs) return;
    try {
      setDisabled('initialEstimatedHrs');
      await updateProject(projectDetails?.id, { initialEstimatedHrs });
      setProjectDetails((prev) => ({ ...prev, initialEstimatedHrs }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleCheckChange = (name) => async (event) => {
    try {
      if (!isEditPermission) return;
      const check = event.target.checked;
      setDisabled(name);
      await updateProject(projectDetails?.id, { [name]: check });
      setProjectDetails((prev) => ({ ...prev, [name]: check }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleDateChange = (name) => async (value) => {
    try {
      if (!isEditPermission) return;
      setDisabled(name);
      const formattedValue = value ? dayjs(value).format('YYYY-MM-DD') : null;

      // Fetch the current start and end dates for comparison
      const startDate = projectDetails?.startDate ? dayjs(projectDetails.startDate, 'YYYY-MM-DD') : null;
      const endDate = projectDetails?.endDate ? dayjs(projectDetails.endDate, 'YYYY-MM-DD') : null;
      const actualStartDate = projectDetails?.actualStartDate
        ? dayjs(projectDetails.actualStartDate, 'YYYY-MM-DD')
        : null;
      const actualEndDate = projectDetails?.actualEndDate ? dayjs(projectDetails.actualEndDate, 'YYYY-MM-DD') : null;

      // Validation for date fields
      if (name === 'endDate' && value && startDate && dayjs(value).isBefore(startDate)) {
        toast.error('End Date cannot be before Start Date.');
        setDisabled(false);
        return;
      }
      if (name === 'actualEndDate' && value && actualStartDate && dayjs(value).isBefore(actualStartDate)) {
        toast.error('Actual End Date cannot be before Actual Start Date.');
        setDisabled(false);
        return;
      }
      if (name === 'startDate' && value && endDate && dayjs(value).isAfter(endDate)) {
        toast.error('Start Date cannot be after End Date.');
        setDisabled(false);
        return;
      }
      if (name === 'actualStartDate' && value && actualEndDate && dayjs(value).isAfter(actualEndDate)) {
        toast.error('Actual Start Date cannot be after Actual End Date.');
        setDisabled(false);
        return;
      }

      await updateProject(projectDetails?.id, { [name]: formattedValue });
      setProjectDetails((prev) => ({ ...prev, [name]: formattedValue }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => setEnteredTotalHrs(projectDetails?.totalHrs), [projectDetails?.totalHrs]);

  useEffect(() => {
    const fetchClientsData = async () => {
      try {
        const response = await getAllClients();
        const data = response?.data?.data?.rows;
        const client = data && data.find((client) => client.id === projectDetails?.clientId);
        setSelectedClient(client);
        setClientList(data);
      } catch (error) {
        const errMsg =
          error?.response?.data?.message || 'An error occurred while fetching the client list. Please try again later.';
        toast.error(errMsg);
      }
    };

    fetchClientsData();
  }, [projectDetails?.clientId]);

  return (
    <Accordion className={classes?.accordion} expanded>
      <AccordionSummary className={classes?.summary}>
        <Typography variant="h4" color="GrayText">
          Project Details
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Typography variant="h6">Client</Typography>
            <Autocomplete
              size="small"
              disableClearable
              disabled={!isEditPermission || disabled === 'clientId'}
              options={clientList}
              getOptionLabel={(option) => option.clientName}
              value={selectedClient}
              onChange={handleClientChange}
              autoComplete
              includeInputInList
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="h6">Status</Typography>
            <Select
              value={projectDetails?.status}
              onChange={handleStatusChange}
              size="small"
              fullWidth
              sx={{ textTransform: 'capitalize' }}
              disabled={!isEditPermission || disabled === 'status'}
            >
              {PROJECT_STATUS_LIST.sort().map((list) => (
                <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={list}>
                  {list}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="h6">Project Estimated Hours</Typography>
            <TextField
              fullWidth
              type="text"
              size="small"
              autoComplete="off"
              inputMode="numeric"
              disabled={!isEditPermission || disabled === 'initialEstimatedHrs'}
              value={enteredInitialEstimatedHrs}
              onChange={handleInitialEstimatedHrsChange}
              onBlur={handleInitialEstimatedHrsBlur}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="h6">Budgeted Hours</Typography>
            <TextField
              fullWidth
              type="text"
              size="small"
              autoComplete="off"
              inputMode="numeric"
              disabled={!isEditPermission || disabled === 'totalHrs'}
              value={enteredTotalHrs}
              onChange={hanldeTotalHrsChange}
              onBlur={handleTotalHrsBlur}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Planned Start Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="startDate"
                value={projectDetails?.startDate}
                onChange={(newValue) => handleDateChange('startDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            {/* Empty space */}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Planned End Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="endDate"
                value={projectDetails?.endDate}
                onChange={(newValue) => handleDateChange('endDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Actual Start Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="actualStartDate"
                value={projectDetails?.actualStartDate}
                onChange={(newValue) => handleDateChange('actualStartDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            {/* Empty space */}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Actual End Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="actualEndDate"
                value={projectDetails?.actualEndDate}
                onChange={(newValue) => handleDateChange('actualEndDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={projectDetails?.allowOverSpent}
                {...label}
                disabled={!isEditPermission || disabled === 'allowOverSpent'}
                onChange={handleCheckChange('allowOverSpent')}
              />
              <Typography className={classes.checkBoxText}>Allow Excess Hours</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={projectDetails?.allowManualTask}
                {...label}
                disabled={!isEditPermission || disabled === 'allowManualTask'}
                onChange={handleCheckChange('allowManualTask')}
              />
              <Typography className={classes.checkBoxText}>Allow Manual Task Entry</Typography>
            </Stack>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default MoreDetails;
